/**
 * @file services/api/configService.ts
 * @brief System configuration API service
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';

export interface SystemConfig {
  id: string;
  key: string;
  value: unknown;
  rawValue: string;
  type: 'task' | 'project' | 'system' | 'notification' | 'performance';
  valueType: 'number' | 'string' | 'boolean' | 'json';
  displayName: string;
  description?: string;
  minValue?: number;
  maxValue?: number;
  defaultValue: string;
  isEditable: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ConfigHistory {
  id: string;
  configId: string;
  oldValue?: string;
  newValue: string;
  changedBy: string;
  changedByName?: string;
  changedAt: string;
  reason?: string;
}

/**
 * Get all system configurations
 */
export async function getAllConfigs(): Promise<SystemConfig[]> {
  const response = await apiClient.get(API_ENDPOINTS.CONFIG.BASE);
  const data = unwrapResponse<SystemConfig[]>(response);
  return Array.isArray(data) ? data : [];
}

/**
 * Get configurations by type
 */
export async function getConfigsByType(type: string): Promise<SystemConfig[]> {
  const response = await apiClient.get(API_ENDPOINTS.CONFIG.BY_TYPE(type));
  const data = unwrapResponse<SystemConfig[]>(response);
  return Array.isArray(data) ? data : [];
}

/**
 * Get a single configuration by key
 */
export async function getConfigByKey(key: string): Promise<SystemConfig> {
  const response = await apiClient.get(API_ENDPOINTS.CONFIG.GET(key));
  return unwrapResponse<SystemConfig>(response);
}

/**
 * Update a configuration value
 */
export async function updateConfig(key: string, value: unknown, reason?: string): Promise<SystemConfig> {
  const response = await apiClient.put(API_ENDPOINTS.CONFIG.UPDATE(key), {
    value,
    reason,
  });
  return unwrapResponse<SystemConfig>(response);
}

/**
 * Reset a configuration to default
 */
export async function resetConfig(key: string): Promise<SystemConfig> {
  const response = await apiClient.post(API_ENDPOINTS.CONFIG.RESET(key));
  return unwrapResponse<SystemConfig>(response);
}

/**
 * Get configuration change history
 */
export async function getConfigHistory(key: string, limit = 50): Promise<ConfigHistory[]> {
  const response = await apiClient.get(API_ENDPOINTS.CONFIG.HISTORY(key), {
    params: { limit },
  });
  const data = unwrapResponse<ConfigHistory[]>(response);
  return Array.isArray(data) ? data : [];
}

/**
 * Bulk update multiple configurations
 */
export async function bulkUpdateConfigs(
  updates: Array<{ key: string; value: unknown }>,
  reason?: string
): Promise<SystemConfig[]> {
  const response = await apiClient.put(API_ENDPOINTS.CONFIG.BASE, {
    updates,
    reason,
  });
  const data = unwrapResponse<SystemConfig[]>(response);
  return Array.isArray(data) ? data : [];
}

export const configService = {
  getAllConfigs,
  getConfigsByType,
  getConfigByKey,
  updateConfig,
  resetConfig,
  getConfigHistory,
  bulkUpdateConfigs,
};

export default configService;
